import { useUser } from "@/OneLiners";
import {
    ActionResponse,
    ActionStatusHelper,
    DOMAIN_CONFIGS,
    DomainFormHook,
    DomainHookProps,
    useAppEnum,
    useFormLifeCycle,
} from "@/Shared";

import { UserFormViewModel } from "../ViewModels";
import { UserValidationSchema } from "../UserFormValidation";
import { useUserEnricher } from "./useUserEnricher";
import { UserModel } from "../UserModel";


/**
 * useUserForm hook – orchestrates user form initialization and submission.
 * 
 * Author: Samano CASTRE  
 * Date: July 17, 2025
 * 
 * Responsibilities:
 * - Coordinates permissions, domain state and enrichment layers
 * - Builds and exposes the UserFormViewModel to the UI
 * - Delegates submission logic and feedback handling to dedicated hooks
 * 
 * Design notes:
 * - This hook does not perform CRUD or permission resolution directly
 * - It acts as an orchestration layer between UI and domain-specific hooks
 */
export const useUserForm = (props: DomainHookProps): DomainFormHook => {
    const { permissionHook } = props;
    const { domainType } = DOMAIN_CONFIGS.User;

    /**
     * User domain operations and related backend state.
     * Exposes CRUD actions, current entity fetching and associated responses.
     */
    const userHook = useUser();

    const {
        userMap,
        getEnrichedUser
    } = useUserEnricher();


    /**
     * User-related enums exposed to the UI layer.
     * Used to build the form view model.
     */
    const { UserEnums } = useAppEnum();


    /**
     * Aggregated response representing the global initialization state.
     * Combines:
     * - user domain state
     * - permission loading state
     * - enrichment layer state
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            userHook.stateResponse.status,
            permissionHook.stateResponse.status
        ),
        error: userHook.stateResponse.error
    };

    const lifeCycleHook = useFormLifeCycle({
        response,
        domainType,
        props,
        entityHook: userHook,
        buildVM: (currentEntity: UserModel) => {
            return new UserFormViewModel(
                UserEnums,
                userMap,
                getEnrichedUser(currentEntity)
            )
        },
    });

    /**
     * Public API exposed to the component layer.
     * Only data and actions strictly required by the UI are returned.
     */
    return {
        // Form
        ...lifeCycleHook,
        viewEnums: UserEnums,
        ValidationSchema: UserValidationSchema(UserEnums),
    }
}